'use client';

import { useWindowSize } from '@/hooks/useWindowSize';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import { RiSparkling2Line } from 'react-icons/ri';
import { FaComments, FaVideo } from 'react-icons/fa';
import { useTranslation } from '@/hooks/useTranslation';
import { MdCall } from 'react-icons/md';
import { HiOutlineCursorClick } from 'react-icons/hi';
import { useModal } from '@/components/ModalContext/ModalContext';

type Props = { locale: string };

const HeroHome = ({ locale }: Props) => {
  const { t } = useTranslation(locale);
  const [width, height] = useWindowSize();

  const liveTest = [
    {
      botType: 'TextBot',
      text: t('Home/Section11/key5'),
      icon: FaComments,
      color: 'tw-text-blue-400',
      btnDemoTarget: 'discover-chatbot-text',
    },
    {
      botType: 'CallBot',
      text: t('Home/Section11/key6'),
      icon: MdCall,
      color: 'tw-text-green-400',
      btnDemoTarget: 'discover-callbot',
    },
    {
      botType: 'VideoBot',
      text: t('Home/Section11/key7'),
      icon: FaVideo,
      color: 'tw-text-pink-400',
      btnDemoTarget: 'discover-videobot',
    },
  ];

  const target = t('Home/Section11/tippingWords', { returnObjects: true }) as Array<any>;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState(target[0]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const { openModal } = useModal();

  useEffect(() => {
    const currentWord = target[currentIndex];

    const typewriterEffect = () => {
      if (isDeleting) {
        // Suppression lettre par lettre
        if (displayedText.length > 0) {
          setDisplayedText((prev) => prev.slice(0, -1));
        } else {
          // Mot complètement supprimé, passer au suivant
          setIsDeleting(false);
          setCurrentIndex((prev) => (prev + 1) % target.length);
          setIsTyping(true);
        }
      } else {
        // Ajout lettre par lettre
        if (displayedText.length < currentWord.length) {
          setDisplayedText(currentWord.slice(0, displayedText.length + 1));
        } else {
          // Mot complètement écrit, attendre puis supprimer
          setTimeout(() => {
            setIsDeleting(true);
            setIsTyping(false);
          }, 2000); // Attendre 2 secondes avant de supprimer
        }
      }
    };

    const typingSpeed = isDeleting ? 50 : 100; // Suppression plus rapide que l'écriture
    const timeout = setTimeout(typewriterEffect, typingSpeed);

    return () => clearTimeout(timeout);
  }, [displayedText, isDeleting, currentIndex, target, isTyping]);

  const handleOpenDemoModal = (btnDemoTarget: string) => {
    switch (btnDemoTarget) {
      case 'discover-chatbot-text':
        openModal('DISCOVER_CHATBOT_TEXT', {});
        return;
      case 'discover-videobot':
        openModal('DISCOVER_VIDEOBOT', {});
        return;
      case 'discover-callbot':
        openModal('DISCOVER_CALLBOT', {});
        return;
      default:
        return;
    }
  };

  const renderAnimatedSubtitle = () => {
    const UNIQUE_PLACEHOLDER = 'agencies';
    const subtitleText = t('Home/Section11/subtitle', { agencies: UNIQUE_PLACEHOLDER });
    const parts = subtitleText.split(UNIQUE_PLACEHOLDER);

    return (
      <>
        {parts[0]}
        <span className="tw-relative tw-inline-block tw-align-bottom tw-min-w-[120px] tw-text-left">
          <span className="tw-text-blue-400 tw-font-bold tw-whitespace-nowrap">{displayedText}</span>
          <span
            className="tw-border-r-2 tw-border-gray-300 tw-ml-1 tw-inline-block tw-h-[1.2em] tw-align-middle"
            style={{
              animation: 'blink 1s infinite',
            }}
          />
        </span>
        <br />
        {parts[1] || ''}
      </>
    );
  };

  return (
    <section className="tw-relative tw-min-h-[90vh] tw-bg-gradient-to-br tw-from-gray-900 tw-via-gray-800 tw-to-gray-900 tw-overflow-hidden tw-pt-2 tw-pb-8">
      {/* Background Effects */}
      <div className="tw-absolute tw-inset-0">
        {/* Animated gradient orbs */}
        <div className="tw-absolute tw-top-1/4 tw-left-1/4 tw-w-72 tw-h-72 md:tw-w-96 md:tw-h-96 tw-bg-blue-500/10 tw-rounded-full tw-blur-3xl tw-animate-pulse"></div>
        <div className="tw-absolute tw-bottom-1/4 tw-right-1/4 tw-w-60 tw-h-60 md:tw-w-80 md:tw-h-80 tw-bg-purple-500/10 tw-rounded-full tw-blur-3xl tw-animate-pulse tw-animation-delay-1000"></div>
        <div className="tw-absolute tw-top-1/2 tw-left-1/2 tw-transform tw--translate-x-1/2 tw--translate-y-1/2 tw-w-48 tw-h-48 md:tw-w-64 md:tw-h-64 tw-bg-green-500/5 tw-rounded-full tw-blur-2xl tw-animate-pulse tw-animation-delay-2000"></div>

        {/* Grid pattern overlay */}
        <div
          className="tw-absolute tw-inset-0 tw-opacity-5"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)`,
            backgroundSize: '50px 50px',
          }}
        ></div>
      </div>

      <div className="tw-relative tw-z-10 tw-w-full tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8">
        <div className="tw-grid lg:tw-grid-cols-12 tw-gap-6 lg:tw-gap-8  tw-py-4 md:tw-py-10 lg:tw-py-20">
          {/* Left Image - Hidden on mobile */}
          <div className="tw-hidden lg:tw-block lg:tw-col-span-3 tw-relative">
            <div className="tw-relative tw-group" onClick={() => handleOpenDemoModal('discover-chatbot-text')}>
              <Image
                priority
                quality={100}
                src="https://cdn.1cdn.app/application/LOUISEBOT/25070520470068_bot_text_banner.png"
                alt={t('Home/Section11/left-image-alt')}
                width={378}
                height={654}
                className="tw-w-full tw-h-auto tw-transition-transform tw-duration-700 group-hover:tw-scale-105 tw-drop-shadow-2xl"
              />
              {/* Glow effect */}
              <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-blue-500/20 tw-to-transparent tw-rounded-lg tw-blur-xl tw--z-10"></div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:tw-col-span-6 tw-col-span-12 tw-text-center tw-space-y-6 lg:tw-space-y-8 tw-w-full">
            {/* Top Badge */}

            <div className="tw-animate-fade-in-up tw-w-full tw-flex tw-justify-center">
              <div className="tw-inline-flex tw-items-center tw-gap-2 sm:tw-gap-3 tw-bg-gray-800/80 tw-backdrop-blur-sm tw-rounded-2xl tw-p-2 tw-border tw-border-gray-700/50 tw-max-w-full tw-overflow-hidden">
                <div className="tw-flex tw-items-center tw-gap-1 sm:tw-gap-2 tw-bg-blue-600 tw-px-2 sm:tw-px-4 tw-py-2 tw-rounded-xl tw-flex-shrink-0">
                  <RiSparkling2Line className="tw-w-3 tw-h-3 sm:tw-w-4 sm:tw-h-4 tw-text-white" />
                  <span className="tw-text-white tw-text-xs sm:tw-text-sm tw-font-bold tw-font-['Lexend'] tw-whitespace-nowrap">
                    {t('Home/Section11/key1')}
                  </span>
                </div>
                <span className="tw-text-gray-300 tw-text-xs sm:tw-text-sm tw-font-medium tw-font-['Lexend'] tw-pr-2 sm:tw-pr-4 tw-truncate">
                  {t('Home/Section11/key2')}
                </span>
              </div>
            </div>

            {/* Main Headline */}
            <div className="tw-space-y-6 lg:tw-space-y-6 tw-animate-fade-in-up tw-animation-delay-200 tw-w-full">
              <h1 className="tw-text-5xl lg:tw-text-5xl xl:tw-text-6xl tw-font-bold tw-font-['Lexend'] tw-leading-10 tw-px-2 sm:tw-px-0">
                <span className="tw-text-white tw-block tw-mb-1 sm:tw-mb-2">
                  {t('Home/Section11/key3-1')}{' '}
                  <span className="tw-text-transparent tw-bg-clip-text tw-bg-gradient-to-r tw-from-yellow-400 tw-to-orange-500">
                    {t('Home/Section11/key3-2')}
                  </span>
                </span>
                <span className="tw-text-white tw-block tw-mb-1 sm:tw-mb-2">
                  {t('Home/Section11/key3-3')}{' '}
                  <span className="tw-text-transparent tw-bg-clip-text tw-bg-gradient-to-r tw-from-green-400 tw-to-emerald-500">
                    {t('Home/Section11/key3-4')}
                  </span>
                </span>
                <span className="tw-text-white tw-block">{t('Home/Section11/key3-5')}</span>
              </h1>

              <p className="tw-text-base sm:tw-text-lg lg:tw-text-xl tw-text-gray-300 tw-max-w-2xl tw-mx-auto tw-leading-relaxed tw-font-['Lexend'] tw-px-4 sm:tw-px-0">
                {renderAnimatedSubtitle()}
              </p>
            </div>

            <div className="tw-animate-fade-in-up tw-animation-delay-600 tw-w-full tw-px-4 sm:tw-px-0  tw-pt-10">
              {/* liveTest */}
              <div className="tw-grid grid-cols-1 sm:tw-grid-cols-3 tw-gap-4 sm:tw-gap-6 tw-max-w-2xl tw-mx-auto">
                {liveTest.map((benefit, index) => (
                  <button
                    key={index}
                    className="tw-group tw-bg-white/5 tw-backdrop-blur-sm tw-rounded-2xl tw-p-4 sm:tw-p-6 tw-border tw-border-white/10 tw-transition-all tw-duration-300 hover:tw-bg-white/10 hover:tw-scale-105"
                    onClick={() => handleOpenDemoModal(benefit.btnDemoTarget as string)}
                  >
                    <div className="tw-flex tw-items-center tw-justify-center tw-mb-2 sm:tw-mb-3">
                      <benefit.icon size={25} className={`${benefit.color}`} />
                    </div>
                    <div
                      className={`tw-text-xl sm:tw-text-xl tw-font-bold tw-font-['Lexend'] tw-mb-2 ${benefit.color}`}
                    >
                      {benefit.botType}
                    </div>
                    <p className="tw-text-gray-300 tw-text-xs sm:tw-text-sm tw-font-['Lexend'] tw-leading-relaxed tw-px-2 sm:tw-px-0 tw-flex tw-items-center tw-justify-center tw-gap-1">
                      {benefit.text}
                      <HiOutlineCursorClick size={20} />
                    </p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right Image - Hidden on mobile */}
          <div className="tw-hidden lg:tw-block lg:tw-col-span-3 tw-relative">
            <div className="tw-relative tw-group" onClick={() => handleOpenDemoModal('discover-videobot')}>
              <Image
                quality={100}
                src="https://cdn.1cdn.app/application/LOUISEBOT/24091507401825_24071510273430_Group_2610663.webp"
                alt={t('Home/Section11/right-image-alt')}
                width={378}
                height={654}
                className="tw-w-full tw-h-auto tw-transition-transform tw-duration-700 group-hover:tw-scale-105 tw-drop-shadow-2xl"
                priority={width >= 768}
              />
              {/* Glow effect */}
              <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-purple-500/20 tw-to-transparent tw-rounded-lg tw-blur-xl tw--z-10"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="tw-hidden sm:tw-block tw-absolute tw-bottom-8 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-animate-bounce">
        <div className="tw-w-6 tw-h-10 tw-border-2 tw-border-white/30 tw-rounded-full tw-flex tw-justify-center">
          <div className="tw-w-1 tw-h-3 tw-bg-white/50 tw-rounded-full tw-mt-2 tw-animate-pulse"></div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes blink {
          0%,
          50% {
            opacity: 1;
          }
          51%,
          100% {
            opacity: 0;
          }
        }

        .tw-animate-fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
        }

        .tw-animation-delay-200 {
          animation-delay: 200ms;
        }

        .tw-animation-delay-400 {
          animation-delay: 400ms;
        }

        .tw-animation-delay-600 {
          animation-delay: 600ms;
        }

        .tw-animation-delay-800 {
          animation-delay: 800ms;
        }

        .tw-animation-delay-1000 {
          animation-delay: 1000ms;
        }

        .tw-animation-delay-2000 {
          animation-delay: 2000ms;
        }
      `}</style>
    </section>
  );
};

export default HeroHome;
